/**
 * Browser-visible configuration. Only VITE_ variables reach the bundle (NFR-8):
 * no project ids, bucket names or internal hostnames belong here.
 *
 * Empty VITE_API_BASE_URL means same origin (the nginx container proxies /api).
 */
export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export const BASEMAP_STYLE_URL: string =
  import.meta.env.VITE_BASEMAP_STYLE_URL || 'https://tiles.openfreemap.org/styles/liberty';

/** The basemap for the dark and grey themes, so the map is not a white block among dark panels. */
export const BASEMAP_STYLE_URL_DARK: string =
  import.meta.env.VITE_BASEMAP_STYLE_URL_DARK || 'https://tiles.openfreemap.org/styles/dark';

/** The basemap style for a resolved theme. */
export function basemapStyleUrl(dark: boolean): string {
  return dark ? BASEMAP_STYLE_URL_DARK : BASEMAP_STYLE_URL;
}

/**
 * ADR 0059 (D4, PR-10), amended by ADR 0063: the basemap choices of the map's tools. `vector` is the configured style
 * above; `none` the plain canvas; `imagery` raster tiles. By default imagery is NASA's Global Imagery Browse Services
 * (GIBS), keyless: Blue Marble when zoomed out and VIIRS true colour of the day the map shows when zoomed in, the switch
 * at a configured zoom. A build may name another source (VITE_BASEMAP_IMAGERY_TILES with its attribution and licence)
 * or switch imagery off (VITE_BASEMAP_IMAGERY=off).
 */
export type BasemapChoice = 'vector' | 'imagery' | 'none';

/** The catalogue words of a GIBS layer's credit (map.basemap.gibs.*). */
export type ImageryCreditKey = 'blueMarble' | 'viirs';

export interface ImageryLayer {
  /** An XYZ tile template of 256-pixel raster tiles ({z}, {x}, {y}); `{date}` is the UTC day shown, YYYY-MM-DD. */
  tiles: string;
  /** The source's deepest level: beyond it the map enlarges the last tiles. */
  maxZoom: number;
  /** The template names a day (`{date}`): the map's day is put in (FR-20). */
  dated: boolean;
  /** The words of the layer's credit, for GIBS; null for a configured source (its attribution says it). */
  creditKey: ImageryCreditKey | null;
}

export interface ImageryBasemap {
  /** Drawn below `switchZoom`; null when one layer serves every zoom. */
  overview: ImageryLayer | null;
  /** Drawn from `switchZoom` (at every zoom when there is no overview). */
  detail: ImageryLayer;
  switchZoom: number;
  /** NASA GIBS: the acknowledgement NASA asks for is in the catalogues (map.basemap.gibs.credit). */
  gibs: boolean;
  /** A configured source's credit as its licence asks for it, shown on the credits line (PR-10); null for GIBS. */
  attribution: string | null;
  licence: string | null;
}

/** ADR 0063: NASA GIBS, the EPSG:3857 "best" WMTS REST endpoint (keyless, checked 18 October 2026). */
export const GIBS_ROOT = 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best';
export const GIBS_OVERVIEW: ImageryLayer = {
  tiles: `${GIBS_ROOT}/BlueMarble_ShadedRelief_Bathymetry/default/default/GoogleMapsCompatible_Level8/{z}/{y}/{x}.jpeg`,
  maxZoom: 8,
  dated: false,
  creditKey: 'blueMarble',
};
export const GIBS_DETAIL: ImageryLayer = {
  tiles: `${GIBS_ROOT}/VIIRS_SNPP_CorrectedReflectance_TrueColor/default/{date}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.jpg`,
  maxZoom: 9,
  dated: true,
  creditKey: 'viirs',
};
/** The zoom from which the day's VIIRS picture replaces Blue Marble (VITE_BASEMAP_IMAGERY_SWITCH_ZOOM). */
export const GIBS_SWITCH_ZOOM = 6;

export function imageryBasemapFrom(env: Readonly<Record<string, unknown>>): ImageryBasemap | null {
  const read = (name: string): string => {
    const value = env[name];
    return typeof value === 'string' ? value.trim() : '';
  };
  const zoomOf = (name: string, fallback: number): number => {
    const zoom = Number.parseInt(read(name), 10);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : fallback;
  };
  const template = (value: string): boolean => /^https:\/\//.test(value) && value.includes('{z}');
  if (read('VITE_BASEMAP_IMAGERY').toLowerCase() === 'off') return null;
  const switchZoom = zoomOf('VITE_BASEMAP_IMAGERY_SWITCH_ZOOM', GIBS_SWITCH_ZOOM);
  const tiles = read('VITE_BASEMAP_IMAGERY_TILES');
  if (!tiles) {
    return { overview: GIBS_OVERVIEW, detail: GIBS_DETAIL, switchZoom, gibs: true, attribution: null, licence: null };
  }
  // A configured source: offered only with its template and its attribution (ADR 0059 decision 12).
  const attribution = read('VITE_BASEMAP_IMAGERY_ATTRIBUTION');
  if (!template(tiles) || !attribution) return null;
  const overviewTiles = read('VITE_BASEMAP_IMAGERY_OVERVIEW_TILES');
  const layer = (value: string, maxZoom: number): ImageryLayer => ({ tiles: value, maxZoom, dated: value.includes('{date}'), creditKey: null });
  return {
    overview: template(overviewTiles) ? layer(overviewTiles, zoomOf('VITE_BASEMAP_IMAGERY_OVERVIEW_MAX_ZOOM', switchZoom)) : null,
    detail: layer(tiles, zoomOf('VITE_BASEMAP_IMAGERY_MAX_ZOOM', 18)),
    switchZoom,
    gibs: false,
    attribution,
    licence: read('VITE_BASEMAP_IMAGERY_LICENCE') || null,
  };
}

/** The imagery basemap of this build (GIBS unless configured otherwise), or null (off). */
export const BASEMAP_IMAGERY: ImageryBasemap | null = imageryBasemapFrom(import.meta.env);

/** The choices offered: the vector map, imagery when on, and no basemap. */
export function basemapChoices(imagery: ImageryBasemap | null = BASEMAP_IMAGERY): BasemapChoice[] {
  return imagery ? ['vector', 'imagery', 'none'] : ['vector', 'none'];
}

/** Join a server path (`/api/...`) onto the API origin. Absolute URLs pass through. */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}
